import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { registerECourseStudent } from "../services/auth";
import { ECourseRegistrationData } from "../types/auth";
import { Eye, EyeOff } from "lucide-react";

const ECourseRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ECourseRegistrationData>({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    profileDetails: {},
    referralSource: {
      source: "",
      otherDescription: "",
    },
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const referralSources = [
    { value: "banner", label: "Banner" },
    { value: "billboard", label: "Billboard" },
    { value: "flyers", label: "Flyers" },
    { value: "social_media", label: "Social Media" },
    { value: "friend", label: "Friend" },
    { value: "family", label: "Family" },
    { value: "other", label: "Other" },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name.startsWith("profileDetails.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        profileDetails: {
          ...prev.profileDetails,
          [field]: value,
        },
      }));
    } else if (name.startsWith("referralSource.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        referralSource: {
          ...prev.referralSource,
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      // Clean up referral source data
      const submissionData = {
        ...formData,
        referralSource: formData.referralSource?.source
          ? {
              source: formData.referralSource.source,
              ...(formData.referralSource.source === "other" && {
                otherDescription: formData.referralSource.otherDescription,
              }),
            }
          : undefined,
      };

      const response = await registerECourseStudent(submissionData);

      if (response.success) {
        toast.success(
          "Registration successful! Please check your email to verify your account."
        );
        navigate("/login");
      } else {
        toast.error(response.message || "Registration failed");
      }
    } catch (error: any) {
      console.error("Registration error:", error);

      // Check for validation errors
      const validationErrors = error.response?.data?.errors;
      if (validationErrors && Array.isArray(validationErrors)) {
        // Format validation errors for display
        const errorMessages = validationErrors.map((err: any) => {
          const fieldName = err.path || err.field || "Field";
          const message = err.msg || err.message || "Invalid value";
          return `• ${fieldName}: ${message}`;
        });

        toast.error(
          `Please fix the following errors:\n${errorMessages.join("\n")}`,
          {
            duration: 6000, // Show longer for multiple errors
          }
        );
      } else {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Registration failed. Please try again.";
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 py-8">
      <div className="w-full max-w-2xl p-8 space-y-6 bg-white rounded-lg shadow-md max-h-[80vh] overflow-y-auto custom-scroll">
        {/* Logo and Back Link */}
        <div className="text-center space-y-4">
          <img
            src="/school-logo.png"
            alt="ATIAM College Logo"
            className="mx-auto h-16 w-auto"
          />
          <a
            href="https://atiamcollege.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            ← Back to ATIAM College
          </a>
        </div>

        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">
            Join ATIAM E-Courses
          </h2>
          <p className="mt-2 text-gray-600">
            Create your account to access online courses and learning materials
          </p>

          <p className="mt-2 text-red-600 italic">
            This is in Test Mode, Do not register unless authorized by the
            Admin!
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700"
              >
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
              />
            </div>
            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700"
              >
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
            />
          </div>

          {/* Password */}
          <div className="relative">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700"
            >
              Password *
            </label>
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              minLength={6}
              className="block w-full mt-1 pr-10 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
            />
            <span
              onClick={() => setShowPassword(!showPassword)}
              className="absolute top-1/2 right-3 transform -translate-y-1/2 bg-transparent border-none p-0 bg-none cursor-pointer"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </span>
            <p className="mt-1 text-sm text-gray-500">
              Password must be at least 6 characters long and contain uppercase,
              lowercase, and number.
            </p>
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700"
            >
              Confirm Password *
            </label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="block w-full mt-1 pr-10 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
            />
            <span
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute top-2/3 right-3 transform -translate-y-1/2 bg-transparent border-none p-0 bg-none cursor-pointer"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </span>
          </div>

          {/* Profile Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700"
              >
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="profileDetails.phone"
                required
                value={formData.profileDetails?.phone || ""}
                onChange={handleInputChange}
                className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
              />
            </div>
            <div>
              <label
                htmlFor="dateOfBirth"
                className="block text-sm font-medium text-gray-700"
              >
                Date of Birth
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="profileDetails.dateOfBirth"
                required
                value={formData.profileDetails?.dateOfBirth || ""}
                onChange={handleInputChange}
                className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
              />
            </div>
          </div>

          {/* Referral Source */}
          <div>
            <label
              htmlFor="referralSource"
              className="block text-sm font-medium text-gray-700"
            >
              How did you hear about ATIAM College?
            </label>
            <select
              id="referralSource"
              name="referralSource.source"
              value={formData.referralSource?.source || ""}
              required
              onChange={handleInputChange}
              className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
            >
              <option value="">Select an option</option>
              {referralSources.map((source) => (
                <option key={source.value} value={source.value}>
                  {source.label}
                </option>
              ))}
            </select>
          </div>

          {/* Other Description */}
          {formData.referralSource?.source === "other" && (
            <div>
              <label
                htmlFor="otherDescription"
                className="block text-sm font-medium text-gray-700"
              >
                Please specify *
              </label>
              <input
                type="text"
                id="otherDescription"
                name="referralSource.otherDescription"
                value={formData.referralSource?.otherDescription || ""}
                onChange={handleInputChange}
                required
                className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                placeholder="Please tell us how you heard about us"
              />
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full px-4 py-3 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        {/* Login Link */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ECourseRegistrationPage;
